import React from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  Grid,
  Tooltip,
} from "@mui/material";
import {
  CreditCard,
  Payments,
  AccountBalanceWallet,
  CheckCircle,
  Stars,
  CallSplit,
  Lock,
} from "@mui/icons-material";

/**
 * PaymentMethodSelector Component
 * Single-select payment method with split option
 */
const PaymentMethodSelector = ({
  paymentMethod,
  setPaymentMethod,
  onSplitPaymentClick,
  isWaiter = false,
  hideNone = false,
}) => {
  const theme = useTheme();

  const paymentOptions = [
    {
      value: "none",
      label: "No Payment",
      subtitle: "Pay Later",
      icon: AccountBalanceWallet,
      color: theme.palette.grey[600],
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.grey[600], 0.08)} 0%, ${alpha(theme.palette.grey[800], 0.12)} 100%)`,
    },
    {
      value: "cash",
      label: "Cash",
      subtitle: "Full Amount",
      icon: Payments,
      color: theme.palette.success.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.dark, 0.12)} 100%)`,
    },
    {
      value: "card",
      label: "Card",
      subtitle: "Full Amount",
      icon: CreditCard,
      color: theme.palette.info.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.dark, 0.12)} 100%)`,
    },
    {
      value: "loyalty",
      label: "Loyalty",
      subtitle: "Full Amount",
      icon: Stars,
      color: theme.palette.warning.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.dark, 0.12)} 100%)`,
    },
    {
      value: "split",
      label: "Split Payment",
      subtitle: "Multiple Methods",
      icon: CallSplit,
      color: theme.palette.secondary.main,
      bgGradient: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.dark, 0.12)} 100%)`,
    },
  ];

  const handleSelectPayment = (methodValue) => {
    // Waiters can only select "none" payment method
    if (isWaiter && methodValue !== "none") {
      return; // Prevent selection
    }

    if (methodValue === "split") {
      onSplitPaymentClick();
    } else {
      setPaymentMethod(methodValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2.5,
        background: `linear-gradient(145deg, ${alpha("#fff", 1)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
        border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.info.main, 0.08)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.12)}`,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
          pb: 2.5,
          borderBottom: `2px solid ${alpha(theme.palette.info.main, 0.1)}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <CreditCard sx={{ fontSize: 20, color: "white" }} />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 0.3,
            }}
          >
            Payment Method
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              color: isWaiter
                ? theme.palette.warning.main
                : theme.palette.info.main,
            }}
          >
            {isWaiter
              ? "Waiters can only place orders without payment"
              : "Choose your payment method"}
          </Typography>
        </Box>
      </Box>

      {/* Payment Method Selection Grid */}
      <Grid container spacing={1.5}>
        {paymentOptions
          .filter((o) => !(hideNone && o.value === "none"))
          .map((option) => {
            const isSelected = paymentMethod === option.value;
            const Icon = option.icon;
            const isDisabled = isWaiter && option.value !== "none";

            const paymentCard = (
              <Paper
                elevation={0}
                onClick={() => handleSelectPayment(option.value)}
                sx={{
                  p: 1.5,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  borderRadius: 2,
                  position: "relative",
                  border: `2px solid ${
                    isSelected
                      ? option.color
                      : alpha(theme.palette.grey[300], 0.3)
                  }`,
                  bgcolor: isSelected ? option.bgGradient : "white",
                  background: isSelected ? option.bgGradient : "white",
                  transform: isSelected ? "scale(1.03)" : "scale(1)",
                  boxShadow: isSelected
                    ? `0 4px 16px ${alpha(option.color, 0.25)}`
                    : `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  opacity: isDisabled ? 0.4 : 1,
                  "&:hover": {
                    transform: isDisabled ? "scale(1)" : "scale(1.03)",
                    borderColor: isDisabled
                      ? alpha(theme.palette.grey[300], 0.3)
                      : option.color,
                    boxShadow: isDisabled
                      ? `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`
                      : `0 6px 20px ${alpha(option.color, 0.3)}`,
                  },
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      bgcolor: option.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 2px 8px ${alpha(option.color, 0.4)}`,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 16, color: "white" }} />
                  </Box>
                )}

                {isDisabled && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      bgcolor: theme.palette.grey[400],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.4)}`,
                    }}
                  >
                    <Lock sx={{ fontSize: 14, color: "white" }} />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: alpha(option.color, 0.1),
                      border: `2px solid ${alpha(option.color, 0.2)}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: option.color }} />
                  </Box>

                  <Box
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.text.primary,
                        display: "block",
                        mb: 0.25,
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    >
                      {option.subtitle}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            );

            return (
              <Grid key={option.value} size={{ xs: 6, sm: 4, md: 2.4 }}>
                {isDisabled ? (
                  <Tooltip
                    title="Waiters can only place orders without payment"
                    arrow
                  >
                    {paymentCard}
                  </Tooltip>
                ) : (
                  paymentCard
                )}
              </Grid>
            );
          })}
      </Grid>
    </Paper>
  );
};

export default PaymentMethodSelector;
